import { Component } from '@theme/component';

/**
 * Témoignages avant/après — carrousel en scroll natif.
 *
 * Reprise de la logique fournie par Gabriel : un IntersectionObserver suit la carte
 * visible pour allumer la bonne puce, les flèches décalent d'une carte.
 * Le composant est scopé à sa section, donc plusieurs instances peuvent coexister.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} track
 * @property {HTMLElement[]} cards
 * @property {HTMLElement[]} [dots]
 *
 * @extends {Component<Refs>}
 */
export class TaaCarousel extends Component {
  requiredRefs = ['track'];

  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    const { track, cards, dots } = this.refs;
    if (!cards?.length || !dots?.length) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.intersectionRatio <= 0.5) continue;

          const index = cards.indexOf(/** @type {HTMLElement} */ (entry.target));
          for (const dot of dots) dot.classList.remove('is-active');
          dots[index]?.classList.add('is-active');
        }
      },
      { root: track, threshold: 0.5 }
    );

    for (const card of cards) this.#observer.observe(card);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  /** Appelé par `on:click="/goTo"` sur une puce. */
  goTo(event) {
    const index = Number(event.target.dataset.index);
    this.refs.cards?.[index]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }

  next() {
    this.#scrollBy(1);
  }

  prev() {
    this.#scrollBy(-1);
  }

  #scrollBy(direction) {
    const { track, cards } = this.refs;
    if (!cards?.length) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 18;
    const step = cards[0].getBoundingClientRect().width + gap;
    track.scrollBy({ left: direction * step, behavior: 'smooth' });
  }
}

if (!customElements.get('taa-carousel')) {
  customElements.define('taa-carousel', TaaCarousel);
}
